'use client';

// Which field each showcase room paints, and how its precomputed payload gets
// to the browser.
//
// ONE module, deliberately, because the landing showcase and the in-product
// pre-upsell must not be able to disagree about this. Both render `UpsellRoom`,
// `UpsellRoom` calls `useShowcaseViz`, and neither surface gets a say — so
// "the room on the landing page" and "the room after your solve" are the same
// room showing the same field, by construction rather than by two call sites
// happening to pass matching props.
//
// The assignment lives in contracts/landing/template-showcase.json, keyed by
// the same room ids `components/upsell/rooms.ts` uses. The payload is the
// static hashed bundle that scripts/build-landing-snapshots.ts emits into
// web/public/landing-templates — pre-trimmed to the one layer each room paints,
// which is what makes a field affordable on a marketing page. The backend's
// /api/templates/:id/:variant returns the same data untrimmed at 10-25 MB,
// which is fine for the editor and not fine here.

import { useEffect, useState } from 'react';
import {
  tryParseLandingShowcaseRegistry,
  type LandingShowcaseVizMode,
  type PersistedVizPayload,
} from '@roomtreatment/core';
import registrySource from '../../../../contracts/landing/template-showcase.json';
import {
  fetchLandingManifest,
  fetchLandingTemplateViz,
} from '@/components/landing/showcase/fetchLandingTemplateDetail';

export interface ShowcaseVizSpec {
  mode: LandingShowcaseVizMode;
  /** Inert for `velocity`, which has no frequency axis. */
  modalFreq: number;
  variant: 'bare' | 'treated';
}

const parsed = tryParseLandingShowcaseRegistry(registrySource);

/** A malformed registry must not take down the landing page — the rooms still
 *  render, just without a field. Same posture the registry parser's own
 *  `tryParse` exists for. */
const SPECS: Record<string, ShowcaseVizSpec> = parsed.ok
  ? Object.fromEntries(parsed.registry.templates.map((t) => [
    t.templateId,
    {
      mode: t.slice.scene.vizMode,
      modalFreq: t.slice.scene.modalFreq ?? 80,
      variant: t.slice.scene.variant,
    },
  ]))
  : {};

export function showcaseVizSpec(roomId: string): ShowcaseVizSpec | null {
  return SPECS[roomId] ?? null;
}

/** Module-level, not per-component: `RoomWipe` mounts two `UpsellRoom`s of the
 *  same room, and both surfaces mount a wipe. Without this the same few MB is
 *  requested two to four times per view — `force-cache` would collapse them at
 *  the HTTP layer, but not the parse. */
const inflight = new Map<string, Promise<PersistedVizPayload | null>>();

async function loadViz(roomId: string, variant: 'bare' | 'treated') {
  const manifest = await fetchLandingManifest();
  if (!manifest) return null;
  const entry = manifest.entries.find(
    (e) => e.templateId === roomId && e.variant === variant,
  );
  if (!entry) return null;
  return fetchLandingTemplateViz(entry);
}

export function fetchShowcaseViz(
  roomId: string,
  variant: 'bare' | 'treated',
): Promise<PersistedVizPayload | null> {
  const key = `${roomId}.${variant}`;
  let p = inflight.get(key);
  if (!p) {
    p = loadViz(roomId, variant).catch(() => null);
    inflight.set(key, p);
  }
  return p;
}

export interface ShowcaseVizState {
  viz: PersistedVizPayload | null;
  /** False only while a fetch for THIS room is still outstanding. A room with
   *  no viz assignment is settled immediately — there is nothing to wait for. */
  settled: boolean;
}

/**
 * The room's precomputed field.
 *
 * `settled` matters more than it looks. `useModalViz` reads its `persistedViz`
 * option exactly ONCE, in a `useState` initialiser (`useIdempotentState` is
 * plain `useState` underneath), so a payload that arrives later is never seen —
 * the hook keeps the empty pairs it seeded at mount and the field silently
 * never appears. Reflections hid this for a while because its fetch path has no
 * gate and quietly re-solved from the live doc instead; the volumetric and
 * velocity paths are gated, so they just stayed blank.
 *
 * So callers must hold the scene back until this says `settled`, and mount it
 * with the payload already in hand. See UpsellRoom.
 *
 * The payload is stored WITH the id it belongs to and read back only when that
 * id still matches, so switching tabs cannot show the previous room's field for
 * a frame while the next one loads.
 */
export function useShowcaseViz(roomId: string): ShowcaseVizState {
  const spec = showcaseVizSpec(roomId);
  const [loaded, setLoaded] = useState<{ id: string; viz: PersistedVizPayload | null } | null>(null);

  useEffect(() => {
    if (!spec) return undefined;
    let live = true;
    fetchShowcaseViz(roomId, spec.variant).then((viz) => {
      if (live) setLoaded({ id: roomId, viz });
    });
    return () => { live = false; };
  }, [roomId, spec?.variant]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!spec) return { viz: null, settled: true };
  if (loaded?.id !== roomId) return { viz: null, settled: false };
  return { viz: loaded.viz, settled: true };
}
